import { Citation, Contributor } from '@chegg-wt/core/types/redux-citation-api'
import { ValidatorStates } from '@chegg-wt/core/state/citation/constants'
import { CitationStatus } from '@chegg-wt/core/state/citation/types'
import { ApolloError } from 'apollo-client'
import { trackWithoutThrow } from '@chegg-wt/core/utils/errors'
import {
  GetFormattedCitationsV2,
  GetFormattedCitationsV2_citations_result_contributors as ContributorV2,
} from '@chegg-wt/core/oneGraphQueries/__generated__/GetFormattedCitationsV2'
import { GetFormattedCitations_formattedCitations } from '../GetFormattedCitations'

export const mapFormattedCitationsToCitation = (
  formattedCitations: GetFormattedCitations_formattedCitations[],
  style: string,
  citationStatus?: CitationStatus
): Citation[] => {
  if (!formattedCitations.length) {
    return []
  }

  return formattedCitations.reduce((acc, formattedCitation) => {
    if (!formattedCitation.formattedData) {
      trackWithoutThrow(new Error(`malformed citation: ${formattedCitation.citationId}`))
      return acc
    }

    const citation: Citation = {
      ...formattedCitation.data,
      formatted_data: {
        output: formattedCitation.formattedData.citation,
        style,
      },
      annotation: formattedCitation.annotation,
      footnote: formattedCitation.formattedData?.footnote,
      id: formattedCitation.citationId,
      projectId: formattedCitation.projectId,
      date: formattedCitation.createdAt ? new Date(formattedCitation.createdAt) : null,
    } as Citation

    citation.contributors = formattedCitation.contributors

    if (citationStatus && citationStatus.id === citation.id) {
      switch (citationStatus.status) {
        case ValidatorStates.COMPLETE:
        case ValidatorStates.IGNORED:
          citation.isNew = true
          break
        case ValidatorStates.MISSING:
          citation.isIncomplete = true
          break
        default:
      }
    }

    return [...acc, citation]
  }, [])
}

/**
 * one graph has slight difference in contributor field names such as firstName instead of first, middleName instead of middle, etc. This methods returns one-graph specific fields mapped as regular contributor fields(first, middle, last, etc)
 * contributorsV2 is the one-graph based contributor fields
 */
export const getMappedContributors = (contributorsV2: ContributorV2[]): Contributor[] => {
  if (Array.isArray(contributorsV2) && contributorsV2.length > 0) {
    return contributorsV2.map((contributorV2) => {
      if (!contributorV2) return {}
      const mappedContributor: Contributor = {
        first: contributorV2.firstName || '',
        middle: contributorV2.middleName || '',
        last: contributorV2.lastName || '',
        function: contributorV2.function || '',
        suffix: contributorV2.data || '',
      }
      return mappedContributor
    })
  }
  return []
}

export const mapFormattedCitationsV2toCitation = (
  formattedCitationsResponse: GetFormattedCitationsV2,
  style: string,
  projectId: string,
  citationStatus?: CitationStatus
): Citation[] => {
  const citationsResults = formattedCitationsResponse?.citations?.result
  if (!citationsResults || !citationsResults.length) {
    return []
  }
  return citationsResults.reduce((acc, citationsResult) => {
    if (!citationsResult.formattedCitationDataV2?.citation) {
      trackWithoutThrow(new Error(`malformed citation: ${citationsResult.id}`))
      return acc
    }

    const citation: Citation = {
      ...citationsResult.data,
      formatted_data: {
        output: citationsResult.formattedCitationDataV2.citation,
        style,
      },
      annotation: citationsResult.annotation,
      id: citationsResult.id,
      projectId,
      date: citationsResult.createdAt ? new Date(citationsResult.createdAt) : null,
    } as Citation

    citation.contributors = getMappedContributors(citationsResult.contributors)
    if (citationStatus && citationStatus.id === citation.id) {
      switch (citationStatus.status) {
        case ValidatorStates.COMPLETE:
        case ValidatorStates.IGNORED:
          citation.isNew = true
          break
        case ValidatorStates.MISSING:
          citation.isIncomplete = true
          break
        default:
      }
    }
    return [...acc, citation]
  }, [])
}

export const NOT_FOUND_ERROR = '404: Not Found'

export const getIsNotFoundError = ({ graphQLErrors }: ApolloError): boolean =>
  graphQLErrors.some(({ message }) => message === NOT_FOUND_ERROR)

export const getIsNotFoundErrorByMessage = (
  { graphQLErrors }: ApolloError,
  errorMessage: string
): boolean => graphQLErrors.some(({ message }) => message === errorMessage)
